// Meeting Create/Edit dialog. Owns state + validation; delegates
// rendering to MeetingFormFields. The per-action validation that
// previously lived here is gone — actions are no longer part
// of the meeting schema (they live in the SharePoint minutes document).

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import MeetingFormFields from "./MeetingFormFields";
import {
  buildEmptyMeetingFormState,
  stateFromMeeting,
  toCreateMeetingInput,
  type MeetingFieldErrors,
  type MeetingFormState,
} from "./meetingFormState";
import { isValidIsoDate } from "../shared/isoDateValidation";
import useSeededFormState from "../../../../hooks/useSeededFormState";
import type {
  CreateMeetingInput,
  Meeting,
  UpdateMeetingInput,
} from "../../../../types";

export type MeetingFormMode = "create" | "edit";

interface MeetingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: MeetingFormMode;
  contractId: string;
  source?: Meeting;
  onCreate?: (input: CreateMeetingInput) => Promise<unknown>;
  onUpdate?: (input: UpdateMeetingInput) => Promise<unknown>;
  isSubmitting?: boolean;
  /** Effective end date of the parent contract (admin-maintained).
   *  Threaded through to MeetingFormFields for Task 7's helper text
   *  and serialiser auto-fill. */
  contractEndDate?: string;
}

function validate(state: MeetingFormState): MeetingFieldErrors {
  const errors: MeetingFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  if (!state.meetingType) errors.meetingType = "Meeting type is required";
  // Date is gated by the Recurring toggle — when on, the recurrence
  // rule's start/end dates carry the schedule and the single-meeting
  // date input is hidden. Only validate the single date when the form
  // is in one-off mode.
  if (!state.isRecurring && !isValidIsoDate(state.date)) {
    errors.date = "Enter a valid date";
  }

  if (state.isRecurring && state.recurrenceRule) {
    if (!isValidIsoDate(state.recurrenceRule.startDate)) {
      errors.recurrenceStartDate = "Enter a valid recurrence start date";
    }
    if (
      state.recurrenceRule.endDate &&
      !isValidIsoDate(state.recurrenceRule.endDate)
    ) {
      errors.recurrenceEndDate = "Enter a valid recurrence end date";
    }
    if (
      !errors.recurrenceStartDate &&
      !errors.recurrenceEndDate &&
      state.recurrenceRule.endDate &&
      state.recurrenceRule.endDate < state.recurrenceRule.startDate
    ) {
      errors.recurrenceEndDate = "End date must be on or after start date";
    }
  }

  return errors;
}

function hasErrors(errors: MeetingFieldErrors): boolean {
  return Object.values(errors).some((v) => v !== undefined);
}

const modeTitle: Record<MeetingFormMode, string> = {
  create: "Add meeting",
  edit: "Edit meeting",
};

export default function MeetingFormModal({
  isOpen,
  onClose,
  mode,
  contractId,
  source,
  onCreate,
  onUpdate,
  isSubmitting,
  contractEndDate,
}: MeetingFormModalProps) {
  const [errors, setErrors] = useState<MeetingFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [primaryCopy, setPrimaryCopy] = useState("Create meeting");

  // Re-seed whenever the modal opens or its target changes so editing a
  // different meeting or switching create↔edit always shows fresh data. The
  // modal is eager-mounted (only isOpen toggles) — useSeededFormState resets
  // its guard on close, so reopening with the same key (e.g. create →
  // create) re-seeds instead of showing the previous values.
  const [state, setState] = useSeededFormState<MeetingFormState>(
    isOpen,
    `${mode}:${source?.id ?? "new"}`,
    () =>
      mode === "edit" && source
        ? stateFromMeeting(source)
        : buildEmptyMeetingFormState(),
    () => {
      setErrors({});
      setSubmitError(null);
    },
  );

  const handleChange = (patch: Partial<MeetingFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof MeetingFieldErrors];
      }
      if (keys.includes("recurrenceRule")) {
        delete next.recurrenceStartDate;
        delete next.recurrenceEndDate;
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (hasErrors(v)) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        const input: UpdateMeetingInput = {
          version: source.version,
          ...toCreateMeetingInput(state, contractId),
        };
        await onUpdate(input);
      } else if (mode === "create" && onCreate) {
        await onCreate(toCreateMeetingInput(state, contractId, { contractEndDate }));
      }
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save meeting.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modeTitle[mode]}
      maxWidth="sm:max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <MeetingFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`mt-${mode}`}
          disabled={isSubmitting}
          onPrimaryCopyChange={setPrimaryCopy}
          contractEndDate={contractEndDate}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {mode === "edit" ? "Save changes" : primaryCopy}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
